import type { Order, OrderLookup } from '@akademi/site-builder-runtime';

/**
 * The just-placed order, handed from the checkout page to the confirmation page.
 *
 * <h3>Why a handoff rather than a re-read</h3>
 *
 * <p>The obvious design is to send the shopper to `/order/{number}` and have
 * that page fetch it. It cannot: reading a guest order needs the order number
 * AND both proofs (contact email, delivery postcode), and putting those in a
 * URL would leave one customer's address in browser history, in the referrer
 * of every asset the page loads, and in whatever they paste to a friend. So the
 * order travels in storage instead, and the URL stays `/order-confirmation`
 * with nothing in it.
 *
 * <p>`sessionStorage`, not `localStorage`: this dies with the tab. A shopper's
 * name, address and order lines should not still be readable next week because
 * they once bought a hoodie on a shared machine.
 *
 * <p>Every read is guarded. Private mode, blocked site data and quota all throw
 * on access rather than returning null, and a confirmation page that throws
 * after the money has been taken is the worst possible moment to fail — so a
 * failure here degrades to "look your order up" and never to an error.
 */
const STORAGE_KEY = 'sorsy.order.placed';

/**
 * The order, plus the two proofs that let the confirmation page ask after it.
 *
 * <p>The proofs travel because the confirmation page has real work to do beyond
 * saying thank you: it polls the payment, and paying is authorised the same way
 * reading is — order number, contact email and delivery postcode, all three. The
 * shopper typed all three a moment ago; this saves them typing them again to see
 * whether their own card went through.
 */
export interface PlacedOrder {
  order: Order;
  proof: OrderLookup;
}

export function rememberPlacedOrder(order: Order, proof: OrderLookup): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ order, proof }));
  } catch {
    // The confirmation page falls back to its lookup form. The order is placed
    // either way — this only decides whether the shopper has to type its
    // number, which is on the screen they are about to leave.
  }
}

export function readPlacedOrder(): PlacedOrder | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlacedOrder;
    // A shape check rather than a cast: storage is writable by anything else on
    // this origin, and rendering `undefined.map` would take the page down.
    return parsed && typeof parsed.order?.orderNumber === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function forgetPlacedOrder(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see above */
  }
}
